import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import ValidationDocumentPreviewDialog from './ValidationDocumentPreviewDialog';

import Sidebar from '../Sidebar/Sidebar';
import useAuth from '../../hooks/useAuth';

import {
  getAnonymizationRuns,
  getAnonymizationRunDetail,
} from '../../services/anonymizationRunsService';

const isPaginated = (data) => data && typeof data === 'object' && Array.isArray(data.results);

const formatBytes = (bytes = 0) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('sk-SK');
  } catch {
    return iso;
  }
};

const labelKeySk = (key) => {
  const map = {
    birth_number: 'Rodné číslo',
    name: 'Meno / priezvisko',
    birth_date: 'Dátum narodenia',
    account_number: 'Číslo účtu / IBAN',
    address: 'Adresa',
    phone: 'Telefón',
    email: 'E-mail',
    custom: 'Vlastné slová/frázy',
  };
  return map[key] || key;
};

const BreakdownChips = ({ breakdown }) => {
  if (!breakdown || typeof breakdown !== 'object') return null;
  const entries = Object.entries(breakdown).filter(([, v]) => Number(v) > 0);
  if (!entries.length) return null;

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
      {entries.map(([k, v]) => (
        <Chip 
          key={k} 
          size="small" 
          variant="outlined" 
          label={`${labelKeySk(k)}: ${v}×`} 
          sx={{ borderRadius: 1.5, fontWeight: 600 }} 
        />
      ))}
    </Stack>
  );
};

const ValidationRunDetailDialog = ({ open, onClose, runId, onShowDocument }) => {
  const [loading, setLoading] = useState(false);
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !runId) return;

    let alive = true;
    setLoading(true);
    setError(null);
    setRun(null);

    (async () => {
      try {
        const data = await getAnonymizationRunDetail(runId);
        if (!alive) return;
        setRun(data);
      } catch (e) {
        if (!alive) return;
        setError(e.message || 'Nepodarilo sa načítať detail.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, runId]);

  const counts = run?.metadata?.counts || null;
  const found = Number(run?.anonymized_count || 0) > 0; // reused as found_count

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Detail kontroly (GDPR)</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        {loading && <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />}
        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

        {run && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={run.status === 'succeeded' ? 'Úspešné' : 'Neúspešné'}
                color={run.status === 'succeeded' ? 'success' : 'error'}
                sx={{ borderRadius: 1.5, fontWeight: 600 }}
              />
              {run.anonymization_group_id && (
                <Chip label={`Batch: ${run.anonymization_group_id}`} sx={{ borderRadius: 1.5, fontWeight: 600 }} />
              )}
              <Chip label={formatDateTime(run.created_at)} variant="outlined" sx={{ borderRadius: 1.5, fontWeight: 600 }} />
            </Stack>

            {found ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                <strong style={{ fontWeight: 600 }}>Dokument nie je vhodné zdieľať.</strong> Našli sa citlivé osobné údaje (GDPR).
              </Alert>
            ) : (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Nenašli sa žiadne citlivé osobné údaje podľa pravidiel kontroly.
              </Alert>
            )}

            <Divider />

            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Súbor
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{run.input_filename || '-'}</Typography>
              <Tooltip
                title={
                  run.has_document
                    ? 'Zobraziť pôvodný dokument'
                    : 'Dokument nie je k dispozícii (neuchoval sa alebo uplynula doba uchovávania)'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityOutlinedIcon />}
                    disabled={!run.has_document}
                    onClick={() => onShowDocument && onShowDocument(run)}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    Zobraziť
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            <GridRow label="Počet nálezov" value={String(run.anonymized_count ?? 0)} />
            <GridRow label="Strany" value={String(run.pages ?? 0)} />
            <GridRow label="Veľkosť" value={formatBytes(run.input_size_bytes || 0)} />

            {run.error_message && (
              <>
                <Divider />
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  <strong style={{ fontWeight: 600 }}>Chyba:</strong> {run.error_message}
                </Alert>
              </>
            )}

            {counts && (
              <>
                <Divider />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Rozpis nálezov
                </Typography>
                <BreakdownChips breakdown={counts} />
              </>
            )}

            {run.options && Object.keys(run.options).length > 0 && (
              <>
                <Divider />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Použité nastavenia (snapshot)
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {JSON.stringify(run.options, null, 2)}
                  </pre>
                </Paper>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

const GridRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Stack>
);

const PIIValidationHistoryPage = () => {
  const { isLoggedIn, user, isLoading } = useAuth();

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [search, setSearch] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRun, setPreviewRun] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);

    try {
      const data = await getAnonymizationRuns({
        processType: 'validation',   // ✅ only validation
        ordering: '-created_at',
        search: search || null,
        pageSize: 50,
      });

      const list = isPaginated(data) ? data.results : Array.isArray(data) ? data : [];
      setRuns(list);
    } catch (e) {
      setErr(e.message || 'Nepodarilo sa načítať históriu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || isLoading) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isLoading]);

  const openDetail = (id) => {
    setSelectedRunId(id);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedRunId(null);
  };

  const openPreview = (run) => {
    setPreviewRun(run);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewRun(null);
  };

  const renderSkeleton = () => (
    <>
      <Sidebar user={user} />
      <Container maxWidth="xl">
        <Paper 
          elevation={0} 
          sx={{ p: { xs: 2, md: 4 }, mt: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <Skeleton variant="text" width={340} height={40} />
          <Skeleton variant="rectangular" height={320} sx={{ mt: 2, borderRadius: 2 }} />
        </Paper>
      </Container>
    </>
  );

  if (!isLoggedIn || isLoading) return renderSkeleton();

  return (
    <>
      <Sidebar user={user} />

      <Container maxWidth="xl">
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, md: 4 }, 
            mt: 4, 
            mb: 5,
            borderRadius: 3, 
            border: '1px solid', 
            borderColor: 'divider', 
            bgcolor: 'background.paper' 
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                História kontrol (GDPR)
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Zobrazuje iba kontroly citlivých údajov (validácia).
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 420 } }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Hľadať podľa názvu súboru…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  sx: { borderRadius: 2 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                onClick={load}
                startIcon={<RefreshIcon />}
                disabled={loading}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Obnoviť
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {err && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{err}</Alert>}

          {loading ? (
            <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Dátum</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Súbor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Nálezy</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Stav</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Verdikt</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Detail</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {runs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                          Žiadne záznamy.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    runs.map((r) => {
                      const foundCount = Number(r.anonymized_count || 0);
                      const ok = r.status === 'succeeded';
                      const notShareable = ok && foundCount > 0;

                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                            {formatDateTime(r.created_at)}
                          </TableCell>

                          <TableCell sx={{ maxWidth: 360 }}>
                            <Typography variant="body2" noWrap title={r.input_filename || ''} sx={{ fontWeight: 500 }}>
                              {r.input_filename || '-'}
                            </Typography>
                            {r.anonymization_group_id && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                Batch: {r.anonymization_group_id}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={foundCount}
                              color={foundCount > 0 ? 'error' : 'default'}
                              variant={foundCount > 0 ? 'filled' : 'outlined'}
                              sx={{ borderRadius: 1.5, fontWeight: 600 }}
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={ok ? 'Úspešné' : 'Neúspešné'}
                              color={ok ? 'success' : 'error'}
                              sx={{ borderRadius: 1.5, fontWeight: 600 }}
                            />
                          </TableCell>

                          <TableCell>
                            {ok ? (
                              <Chip
                                size="small"
                                color={notShareable ? 'error' : 'success'}
                                label={notShareable ? 'NEZDIEĽAŤ' : 'OK'}
                                sx={{ borderRadius: 1.5, fontWeight: 600 }}
                              />
                            ) : (
                              <Chip size="small" variant="outlined" label="—" sx={{ borderRadius: 1.5, fontWeight: 600 }} />
                            )}
                          </TableCell>

                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                              <Tooltip
                                title={
                                  r.has_document
                                    ? 'Zobraziť pôvodný dokument'
                                    : 'Dokument nie je k dispozícii (neuchoval sa alebo uplynula doba uchovávania)'
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VisibilityOutlinedIcon />}
                                    disabled={!r.has_document}
                                    onClick={() => openPreview(r)}
                                    sx={{ borderRadius: 2, fontWeight: 600, whiteSpace: 'nowrap' }}
                                  >
                                    Zobraziť
                                  </Button>
                                </span>
                              </Tooltip>

                              <Tooltip title="Detail kontroly">
                                <IconButton
                                  size="small"
                                  onClick={() => openDetail(r.id)}
                                  sx={{ bgcolor: 'action.hover' }}
                                >
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      <ValidationRunDetailDialog
        open={detailOpen}
        onClose={closeDetail}
        runId={selectedRunId}
        onShowDocument={(run) => {
          closeDetail();
          openPreview(run);
        }}
      />

      <ValidationDocumentPreviewDialog open={previewOpen} onClose={closePreview} run={previewRun} />
    </>
  );
};

export default PIIValidationHistoryPage;